import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const test: ContentBlock<"test"> = {
  id: makeId("test-hormone-panel"),
  kind: "test",
  tags: ["testing", "her", "at-home", "pre-cycle"],
  title: "Complete Hormone Panel",
  subtitle: "At-home finger-prick kit - AMH plus day 2-4 FSH, LH, estradiol",
  icon: "🩸",
  body: {
    testName: "Complete Fertility Hormone Panel",
    category: "blood-marker",
    whatItMeasures:
      "AMH estimates ovarian reserve. FSH, LH, and estradiol are interpreted as an early-follicular baseline to show brain-ovary signaling before one follicle takes over.",
    howItWorks:
      "Collect one finger-prick blood sample at home, usually on cycle day 2-4. That timing is for the baseline FSH, LH, and estradiol values. AMH is included in the same draw for convenience, but AMH itself can be measured on any cycle day. The kit includes the collection device, prepaid return envelope, and instructions.",
    quickFacts: [
      { kind: "sample", value: "Finger-prick blood" },
      { kind: "time", value: "10-15 min home collection" },
      { kind: "pain", value: "Lancet pinch" },
      { kind: "prep", value: "Day 2-4 for FSH/LH/E2" },
      { kind: "results", value: "5-7 business days" },
    ],
    turnaroundTime: "5-7 business days",
    costRange: { low: 149, high: 249 },
    atHome: true,
    clinicRequired: false,
    cycleDay:
      "Day 2-4 for baseline FSH, LH, and estradiol; AMH can be drawn any cycle day.",
    markerTiming: [
      {
        marker: "AMH",
        timing: "Any cycle day",
        reason:
          "AMH is relatively stable across the cycle, so it is bundled with day 2-4 labs for convenience rather than because AMH needs that timing.",
      },
      {
        marker: "FSH",
        timing: "Cycle day 2-4, most often day 2 or 3",
        reason:
          "Early follicular testing shows the baseline pituitary signal before a dominant follicle changes the hormone pattern.",
      },
      {
        marker: "LH",
        timing: "Cycle day 2-4 for baseline; mid-cycle only if checking surge",
        reason:
          "Baseline LH helps with ovarian reserve and PMOS (ex PCOS) context. Surge testing is a separate ovulation-timing question.",
      },
      {
        marker: "Estradiol (E2)",
        timing: "Cycle day 2-4 for baseline; repeated during stimulation",
        reason:
          "Early estradiol shows whether the ovaries are quiet and helps interpret FSH because high estradiol can suppress FSH.",
      },
    ],
    whyItMatters: [
      "AMH and AFC establish ovarian reserve context; they are planning markers, not short-term optimization targets.",
      "Day-2 to day-4 FSH, LH, and estradiol show the baseline brain-ovary signal before one follicle dominates the cycle.",
      "A rising day-3 FSH trend, especially with low AMH or AFC, can be a reason to move sooner rather than wait.",
      "This panel does not replace thyroid, vitamin D, prolactin, semen analysis, or ultrasound when those are clinically relevant.",
    ],
    dataPoints: [
      makeId("dp-amh-level"),
      makeId("dp-fsh-level"),
      makeId("dp-lh-level"),
      makeId("dp-estradiol-level"),
    ],
    section: "ovarian-reserve",
  },
  sources: [
    {
      title: "Ovarian Reserve: Predicting Fertility Potential in Women",
      authors: "ASRM Patient Education",
      journal: "ReproductiveFacts.org (ASRM)",
      year: 2024,
      url: "https://www.reproductivefacts.org/news-and-publications/fact-sheets-and-infographics/ovarian-reserve-predicting-fertility-potential-in-women/",
      evidenceLevel: "guideline",
    },
    {
      title:
        "Testing and interpreting measures of ovarian reserve: a committee opinion",
      authors:
        "Practice Committee of the American Society for Reproductive Medicine",
      journal: "Fertility and Sterility",
      year: 2020,
      url: "https://www.asrm.org/practice-guidance/practice-committee-documents/testing-and-interpreting-measures-of-ovarian-reserve-a-committee-opinion-2020/",
      evidenceLevel: "guideline",
    },
  ],
  readTimeMinutes: 4,
};

registerBlock(test);
